/**
 * Wave 1 Brand Refresh repaint of the legacy SystemNavigation — keeps its
 * behavior, only swaps visuals to V3 tokens.
 *
 * https://syf-component-library.app.dev1.use1.pcf.syfbank.com/?path=/docs/brand-refresh-wave-1-migration-guide--docs
 * #step-3--header--navigation-updates
 */
import styled from 'styled-components'

import SystemNavigation from 'syf-component-library/ui/patterns/SystemNavigation'
import {
  LogoContainer,
  ProfileContainer,
  ProfileContent,
  VerticalLine
} from 'syf-component-library/ui/patterns/SystemNavigation/subcomponents'
import query from 'syf-component-library/const/mediaQueries'

const V3SystemNavigation = styled(SystemNavigation)`
  background-color: var(--syfds-color-background-brand-default);

  & > div {
    max-width: 1248px;
    width: 100%;
    margin: 0 96px;
    padding-left: 0;
    padding-right: 0;

    @media ${query.lessThanHuge} {
      margin: 0 48px;
    }
    @media ${query.lessThanLarge} {
      margin: 0 24px;
    }
    @media ${query.lessThanMedium} {
      margin: 0 16px;
      /* Let the header row shrink to the viewport instead of its content. */
      min-width: 0;
    }
  }

  #alerts-button span {
    display: flex;
    align-items: center;
  }

  /* --- Nav text buttons: language, alerts, back --- */
  ${ProfileContent} > #language-button,
  #alerts-button,
  #back-button {
    all: unset;
    box-sizing: border-box;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--syfds-spacing-button-gap);
    border-radius: var(--syfds-button-border-radius-md);
    border: none;
    cursor: pointer;
    width: fit-content;
    text-decoration: none;

    font-family: var(--syfds-typography-label-large-emphasized-font-family);
    font-size: var(--syfds-typography-label-large-emphasized-font-size);
    font-weight: var(--syfds-typography-label-large-emphasized-font-weight);
    line-height: var(--syfds-typography-label-large-emphasized-line-height);
    letter-spacing: var(
      --syfds-typography-label-large-emphasized-letter-spacing
    );

    padding: var(--syfds-spacing-button-vert-padding-lg)
      var(--syfds-spacing-button-horiz-padding-lg);
    min-width: var(--syfds-spacing-button-min-width-lg);
    height: var(--syfds-size-component-xlg);

    &::after {
      background-image: none;
      transform: none;
      transition: none;
    }

    &:disabled {
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: none;
      &::after {
        content: '';
        display: block;
        position: absolute;
        inset: -4px;
        width: auto;
        height: auto;
        background-image: none;
        background-color: transparent;
        border: var(--syfds-focus-ring-border-width-default) solid
          var(--syfds-focus-ring-color-border-default);
        border-radius: var(--syfds-focus-ring-border-radius-sm);
        pointer-events: none;
        transform: none;
        opacity: 1;
        transition: none;
      }
    }

    &:focus-visible {
      outline: none;
      &::after {
        content: '';
        display: block;
        position: absolute;
        inset: -4px;
        width: auto;
        height: auto;
        background-image: none;
        background-color: transparent;
        border: var(--syfds-focus-ring-border-width-default) solid
          var(--syfds-focus-ring-color-border-default);
        border-radius: var(--syfds-focus-ring-border-radius-sm);
        pointer-events: none;
        transform: none;
        opacity: 1;
        transition: none;
      }
    }

    & > span {
      margin: 0;
      display: flex;
      align-items: center;
    }

    display: flex;
    gap: 8px;
    border-radius: var(--syfds-button-border-radius-lg);
    padding: 4px 8px;
    height: 40px;

    background-color: var(
      --syfds-button-tertiary-color-background-brand-default
    );
    color: var(--syfds-button-tertiary-color-content-brand-default);

    &:hover:not(:disabled) {
      background-color: var(
        --syfds-button-tertiary-color-background-brand-hover
      );
      color: var(--syfds-button-tertiary-color-content-brand-hover);
    }
    &:active:not(:disabled) {
      background-color: var(
        --syfds-button-tertiary-color-background-brand-active
      );
      color: var(--syfds-button-tertiary-color-content-brand-active);
    }
    &:disabled {
      color: var(--syfds-button-tertiary-color-content-disabled);
    }

    &,
    & * {
      font-family: var(--syfds-typography-body-large-emphasized-font-family);
      font-size: var(--syfds-typography-body-large-emphasized-font-size);
      font-weight: var(--syfds-typography-body-large-emphasized-font-weight);
      line-height: var(--syfds-typography-body-large-emphasized-line-height);
      letter-spacing: var(
        --syfds-typography-body-large-emphasized-letter-spacing
      );
      color: var(--syfds-button-tertiary-color-content-brand-default);
    }

    & svg {
      width: 20px;
      height: 20px;
      font-size: 20px;
    }

    &:hover,
    &:hover * {
      color: var(--syfds-button-tertiary-color-content-brand-hover);
    }
    &:active,
    &:active * {
      color: var(--syfds-button-tertiary-color-content-brand-active);
    }
  }

  /* Back button uses label-large-emphasized typography (overrides
     the body-large-emphasized that navTextButtonOverrides applied). */
  #back-button {
    &,
    & * {
      font-family: var(--syfds-typography-label-large-emphasized-font-family);
      font-size: var(--syfds-typography-label-large-emphasized-font-size);
      font-weight: var(--syfds-typography-label-large-emphasized-font-weight);
      line-height: var(--syfds-typography-label-large-emphasized-line-height);
      letter-spacing: var(
        --syfds-typography-label-large-emphasized-letter-spacing
      );
    }
  }

  /* --- Back button left-edge divider (pseudo-element) --- */
  #back-button::before {
    content: '';
    display: inline-block;
    width: 1px;
    height: 100%;
    position: absolute;
    left: 0;
    background-color: var(--syfds-color-background-knockout-default);
  }

  /* --- Vertical dividers --- */
  ${VerticalLine} {
    border-right: 1px solid var(--syfds-color-background-knockout-default);
    height: 40px;
  }

  /* --- Language label (non-button text beside the globe icon) --- */
  p[language] {
    border-left: 1px solid var(--syfds-color-background-knockout-default);
    height: 40px;
    color: var(--syfds-color-content-default);
    font-family: var(--syfds-typography-body-large-font-family);
    font-size: var(--syfds-typography-body-large-font-size);
    font-weight: var(--syfds-typography-body-large-font-weight);
    line-height: var(--syfds-typography-body-large-line-height);
    letter-spacing: var(--syfds-typography-body-large-letter-spacing);
  }

  /* --- Profile menu toggle (icon button) + v3 focus ring --- */
  /* Search's combobox opens its own listbox, not a menu — keep its styles. */
  & [aria-expanded]:not([aria-controls='search-results-listbox']),
  & [aria-label='Open Profile Menu'] {
    all: unset;
    margin-left: -4px;
    &,
    & * {
      font-family: var(--syfds-typography-body-large-emphasized-font-family);
      font-size: var(--syfds-typography-body-large-emphasized-font-size);
      font-weight: var(--syfds-typography-body-large-emphasized-font-weight);
      line-height: var(--syfds-typography-body-large-emphasized-line-height);
      letter-spacing: var(
        --syfds-typography-body-large-emphasized-letter-spacing
      );
      color: var(--syfds-button-tertiary-color-content-brand-default);
    }

    /* position:relative needed for the focus-ring ::after */
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    height: 40px;
    padding: 4px;
    box-sizing: border-box;
    border-radius: var(--syfds-button-border-radius-lg);

    /* Neutralize v1 ::after so the focus ring can take over */
    &::after {
      background-image: none !important;
      transform: none !important;
      transition: none !important;
    }

    & svg {
      width: 20px;
      height: 20px;
      font-size: 20px;
    }

    &:focus-visible {
      outline: none;
      &::after {
        content: '';
        display: block;
        position: absolute;
        inset: -4px;
        width: auto;
        height: auto;
        background-image: none;
        background-color: transparent;
        border: var(--syfds-focus-ring-border-width-default) solid
          var(--syfds-focus-ring-color-border-default);
        border-radius: var(--syfds-focus-ring-border-radius-sm);
        pointer-events: none;
        transform: none;
        opacity: 1;
        transition: none;
      }
    }

    &:hover {
      background-color: var(--syfds-icon-button-ghost-color-background-hover);
      cursor: pointer;
    }
    &:active {
      background-color: var(--syfds-icon-button-ghost-color-background-active);
    }

    & span {
      padding-right: 0;
      display: flex;
      align-items: center;
    }

    @media ${query.lessThanMedium} {
      width: 40px;
      display: flex;
      justify-content: center;
      box-sizing: border-box;
    }
  }

  /* --- Logo link — v3 focus ring on the <a> wrapping the <img> --- */
  & ${LogoContainer} a {
    position: relative;
    border-radius: var(--syfds-focus-ring-border-radius-sm);
    padding: 0;
    height: 40px;
    &:focus-visible {
      outline: none;
      &::after {
        content: '';
        display: block;
        position: absolute;
        inset: -4px;
        width: auto;
        height: auto;
        background-image: none;
        background-color: transparent;
        border: var(--syfds-focus-ring-border-width-default) solid
          var(--syfds-focus-ring-color-border-default);
        border-radius: var(--syfds-focus-ring-border-radius-sm);
        pointer-events: none;
        transform: none;
        opacity: 1;
        transition: none;
      }
    }

    & img {
      height: 36px;
    }
  }

  /* --- Mobile layout adjustments --- */
  @media ${query.lessThanMedium} {
    padding-top: 0;
    align-items: center;
    display: flex;
    justify-content: space-between;

    /* Alerts moves into the dropdown menu on mobile */
    #alerts-button {
      display: none;
    }

    /* Hide back button when Search is expanded to free up header space. */
    &:has([data-test='search-container'][data-expanded='true']) #back-button {
      display: none;
    }

    /* Let Search shrink instead of overflowing the header row. */
    ${ProfileContainer} {
      min-width: 0;
    }
  }

  /* --- Tooltip and dropdown overrides for nav buttons (alerts, profile menu) --- */

  /* Alerts tooltip --- */
  & #tooltip-alerts-tooltip {
    width: 240px;

    p {
      color: var(--syfds-color-content-default);
    }
  }

  & ul {
    padding: 8px 0;
    border-radius: var(--syfds-border-radius-2xlg);
    min-width: 282px;

    /* Dropdown menu items desktop */

    & li:last-child div {
      margin-top: 8px;

      & button {
        margin-top: 8px;
      }
    }

    & li button {
      width: 100%;
      display: flex;
      align-items: center;
      min-height: 48px;
      margin: 0;
      border-radius: var(--syfds-button-border-radius-lg);

      &:hover {
        background-color: var(
          --syfds-button-tertiary-color-background-brand-hover
        );
      }

      &:active {
        background-color: var(
          --syfds-button-tertiary-color-background-brand-active
        );
      }

      & p,
      & span {
        color: var(--syfds-button-tertiary-color-content-brand-default);
        font-family: var(--syfds-typography-label-large-emphasized-font-family);
        font-size: var(--syfds-typography-label-large-emphasized-font-size);
        font-weight: var(--syfds-typography-label-large-emphasized-font-weight);
        line-height: var(--syfds-typography-label-large-emphasized-line-height);
        letter-spacing: var(
          --syfds-typography-label-large-emphasized-letter-spacing
        );
      }

      & svg {
        display: flex;
        align-items: center;
        width: 20px;
        height: 20px;
        margin-right: 0;
      }
    }
  }

  /* SlideInModalPortal overrides (used in mobile) */
  & #system-navigation-modal {
    & > div:first-child {
      height: 48px;

      svg {
        color: var(--syfds-color-content-default);
      }
    }

    ul {
      padding: 0;

      button {
        gap: 8px;

        & span {
          margin-right: 0;
        }
      }

      li:first-child {
        border-top: 1px solid var(--syfds-color-border-default);
        border-bottom: 1px solid var(--syfds-color-border-default);
        height: 64px;
        display: flex;
        align-items: center;
        margin-bottom: 8px;
      }

      li:last-child > div {
        border-top: 1px solid var(--syfds-color-border-default);
        height: 64px;
        margin-top: 8px;
        padding-top: 0;
      }
    }
  }
`

export const V3LogoutSystemNavigation = styled(V3SystemNavigation)`
  background-color: var(--syfds-color-background-page-default);

  & > div {
    position: relative;
    justify-content: flex-end;
  }

  ${LogoContainer} {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  ${VerticalLine} {
    display: none;
  }
`

export { V3SystemNavigation }
